import { useApiClient } from '../../contexts/ApiClientContext'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  GetWorkspaceMembersRequest,
  InviteWorkspaceMembersRequest,
  RemoveWorkspaceMemberRequest,
  ResendInvitationToWorkspaceMemberRequest,
  ResendInvitationToWorkspaceMembersRequest,
} from 'lib/generated/api'
import { mapToWorkspaceMembers } from 'api/mappers/workspaceMember'

export const workspaceMembersQueryKey = ({
  organizationId,
  workspaceId,
  ...queryParams
}: GetWorkspaceMembersRequest) => [
  'workspaceMembers',
  organizationId,
  workspaceId,
  queryParams,
]

export const useGetWorkspaceMembers = (request: GetWorkspaceMembersRequest) => {
  const { workspaceMembersApi } = useApiClient()
  return useQuery(
    workspaceMembersQueryKey(request),
    () => workspaceMembersApi.getWorkspaceMembers(request),
    {
      enabled: !!request.workspaceId && !!request.organizationId,
    }
  )
}

export const useGetWorkspaceMembersMapped = (
  request: GetWorkspaceMembersRequest
) => {
  const { workspaceMembersApi } = useApiClient()
  return useQuery(
    workspaceMembersQueryKey(request),
    () => workspaceMembersApi.getWorkspaceMembers(request),
    {
      enabled: !!request.workspaceId && !!request.organizationId,
      select: mapToWorkspaceMembers,
    }
  )
}

export const useInviteWorkspaceMembers = () => {
  const { workspaceMembersApi } = useApiClient()
  const queryClient = useQueryClient()
  return useMutation(
    (request: InviteWorkspaceMembersRequest) =>
      workspaceMembersApi.inviteWorkspaceMembers(request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(
          workspaceMembersQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}

export const useRemoveWorkspaceMember = () => {
  const { workspaceMembersApi } = useApiClient()
  const queryClient = useQueryClient()
  return useMutation(
    (request: RemoveWorkspaceMemberRequest) =>
      workspaceMembersApi.removeWorkspaceMember(request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(
          workspaceMembersQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}

export const useResendInvitationToWorkspaceMembers = () => {
  const { workspaceMembersApi } = useApiClient()
  const queryClient = useQueryClient()
  return useMutation(
    (request: ResendInvitationToWorkspaceMembersRequest) =>
      workspaceMembersApi.resendInvitationToWorkspaceMembers(request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(workspaceMembersQueryKey(variables))
      },
    }
  )
}

export const useResendInvitationToWorkspaceMember = () => {
  const { workspaceMembersApi } = useApiClient()
  const queryClient = useQueryClient()
  return useMutation(
    (request: ResendInvitationToWorkspaceMemberRequest) =>
      workspaceMembersApi.resendInvitationToWorkspaceMember(request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(workspaceMembersQueryKey(variables))
      },
    }
  )
}
